import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_ENVIRONMENT } from '@shared/providers/environment.provider';
import { map, tap } from 'rxjs';
import {
  downloadFile,
  getFilenameFromContentDisposition,
} from '@shared/utils/download-file';
import { mimeTypes } from '@shared/constants/mime-types';
import { Environment } from '../../types/environment';
import {
  Cargo,
  CargoArchiveJournalFilter,
  CargoArchiveJournalResponse,
  CargoOrder,
  CargoOrderGroup,
  CreateEmptyCargoPayload,
} from '../../types/cargo';
import { ControlRecord, ControlRecordMessageType } from '../../types/control';

@Injectable({
  providedIn: 'root',
})
export class CargoRepositoryService {
  private readonly API_CARGO = `${this.env.apiUrl}/cargo`;
  private readonly API_ORDER_GROUP = `${this.env.apiUrl}/orderGroup`;
  private readonly API_ORDER = `${this.env.apiUrl}/order`;
  constructor(
    @Inject(APP_ENVIRONMENT)
    private readonly env: Environment,
    private readonly http: HttpClient,
  ) {}

  getCargoById(id: number) {
    return this.http.get<Cargo>(`${this.API_CARGO}/getById?id=${id}`);
  }

  validate(id: number) {
    return this.http
      .get<ControlRecord[]>(`${this.API_CARGO}/validate`, {
        params: {
          id,
        },
      })
      .pipe(
        map(items => {
          const res: ControlRecord[] = items.map(item => ({
            ...item,
            message: {
              ...item.message,
              type: item.message.type.toLowerCase() as ControlRecordMessageType,
            },
          }));
          return res;
        }),
      );
  }

  updateCargo(cargo: Cargo) {
    return this.http.post<Cargo>(`${this.API_CARGO}/save`, cargo);
  }

  createOrderGroup(cargoId: number) {
    return this.http.post<CargoOrderGroup>(
      `${this.API_ORDER_GROUP}/create?cargoId=${cargoId}`,
      null,
    );
  }

  updateOrderGroup(orderGroup: CargoOrderGroup) {
    return this.http.post<CargoOrderGroup>(
      `${this.API_ORDER_GROUP}/save`,
      orderGroup,
    );
  }

  deleteOrderGroup(id: number) {
    return this.http.post<void>(
      `${this.API_ORDER_GROUP}/deleteById?id=${id}`,
      null,
    );
  }

  createOrder(orderGroupId: number) {
    return this.http.post<CargoOrder>(
      `${this.API_ORDER}/create?orderGroupId=${orderGroupId}`,
      null,
    );
  }

  updateOrder(order: CargoOrder) {
    return this.http.post<CargoOrder>(`${this.API_ORDER}/save`, order);
  }

  updateOrders(orders: CargoOrder[]) {
    return this.http.post<CargoOrder[]>(`${this.API_ORDER}/updateAll`, orders);
  }

  deleteOrder(id: number) {
    return this.http.post<void>(`${this.API_ORDER}/deleteById?id=${id}`, null);
  }

  createEmptyCargo(payload: {
    preOrderIds: CreateEmptyCargoPayload['preOrderIds'];
    trailerType: CreateEmptyCargoPayload['cargo']['trailerType'];
    transportType: CreateEmptyCargoPayload['cargo']['transportType'];
    divisionId: number;
  }) {
    const cargoPayload: CreateEmptyCargoPayload = {
      cargo: {
        id: null,
        divisionId: payload.divisionId,
        uniqueKey: null,
        creationDate: null,
        uploadDate: null,
        unloadDate: null,
        transportType: payload.transportType,
        trailerType: payload.trailerType,
        sellerJson: {
          id: null,
          uniqueKey: null,
          inn: null,
          name: null,
          nameFromInvoice: null,
          publicAccess: null,
        },
        cargoPlacesQuantityJson: [],
        paymentsJson: [],
        userId: null,
        orderGroups: [],
        loadingAddressesJson: [],
        unloadingAddressesJson: [],
        carrierJson: {
          id: null,
          uniqueKey: null,
          name: null,
          cost: null,
          currencyLetter: null,
          paymentType: null,
          transports: [],
          additionalPayments: [],
        },
        status: null,
        transitPointsJson: [],
        grossWeight: null,
        temperatureRegime: null,
        payments: [],
      },
      preOrderIds: payload.preOrderIds,
    };
    return this.http.post<Cargo>(
      `${this.API_CARGO}/createEmptyCargo`,
      cargoPayload,
    );
  }

  moveFromCalculationToOnTheWay(id: number) {
    return this.http.post(`${this.API_CARGO}/onTheWay?id=${id}`, null);
  }

  getOrdersByCargoId(cargoId: number) {
    return this.http.get<CargoOrder[]>(
      `${this.API_ORDER}/getByCargoId?cargoId=${cargoId}`,
    );
  }

  addPreOrders({ preOrderIds, id }: { preOrderIds: number[]; id: number }) {
    return this.http.post<Cargo>(
      `${this.API_CARGO}/addPreOrders?id=${id}`,
      preOrderIds,
    );
  }

  deletePreOrder({ preOrderId, id }: { preOrderId: number; id: number }) {
    return this.http.post<Cargo>(
      `${this.API_CARGO}/deletePreOrder?id=${id}&preOrderId=${preOrderId}`,
      null,
    );
  }

  recalculateAddresses(id: number) {
    return this.http.post<Cargo>(
      `${this.API_CARGO}/recalculateAddresses?id=${id}`,
      null,
    );
  }

  sendCalculatedCargoTo1c(id: number) {
    return this.http.post(
      `${this.API_CARGO}/sendCalculatedCargoTo1c?id=${id}`,
      null,
    );
  }

  setUserId({ id, userId }: { id: number; userId: number }) {
    return this.http.post(
      `${this.API_CARGO}/setUserId?id=${id}&userId=${userId}`,
      null,
    );
  }

  sendTo1c(id: number) {
    return this.http.post(`${this.API_CARGO}/sendTo1c?id=${id}`, null);
  }

  setRemark({ remark, id }: { remark: Nullable<string>; id: number }) {
    return this.http.post<void>(`${this.API_CARGO}/setRemark?id=${id}`, remark);
  }

  setTrash({ trash, id }: { trash: boolean; id: number }) {
    return this.http.post<void>(
      `${this.API_CARGO}/setTrash?trash=${trash}&id=${id}`,
      null,
    );
  }

  moveToSvh({ id, svhArrivalDate }: { id: number; svhArrivalDate: string }) {
    return this.http.post<void>(
      `${this.API_CARGO}/svh?id=${id}&svhArrivalDate=${svhArrivalDate}`,
      null,
    );
  }

  moveToDelivery({ id }: { id: number }) {
    return this.http.post<void>(`${this.API_CARGO}/delivery?id=${id}`, null);
  }

  setArchive({ id, archive }: { id: number; archive: boolean }) {
    return this.http.post<void>(
      `${this.API_CARGO}/setArchive?id=${id}&archive=${archive}`,
      null,
    );
  }

  getArchive({
    page,
    size,
    filter,
  }: {
    page: number;
    size: number;
    filter: CargoArchiveJournalFilter;
  }) {
    const params: Record<string, any> = { page, size };
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== null && value !== '') {
        params[key] = value;
      }
    });
    return this.http.get<CargoArchiveJournalResponse>(
      this.API_CARGO.concat(`/getArchive`),
      {
        params,
      },
    );
  }

  generateTransportOrder(id: number) {
    return this.http
      .get(`${this.API_CARGO}/generateTransportOrder`, {
        responseType: 'blob',
        observe: 'response',
        params: {
          id,
        },
      })
      .pipe(
        tap(res => {
          if (res.body) {
            const name = getFilenameFromContentDisposition(
              res.headers.get('content-disposition'),
            );
            downloadFile({
              data: res.body,
              type: name.endsWith('.pdf')
                ? mimeTypes.APPLICATION_PDF
                : mimeTypes.DOCX,
              name,
            });
          }
        }),
      );
  }
}
